import { useEffectEvent as useRACEffectEvent } from '@react-aria/utils';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

export interface TableRowState {
  rowKey: string;
  isSelected?: boolean;
  onSelectionToggle?: (value?: boolean) => void;
}

const TableRowStateContext = createContext<TableRowState | null>(null);

export function TableRowStateProvider({
  children,
  rowKey,
  isSelected,
  onSelectionToggle,
}: {
  children: ReactNode;
  rowKey: string;
  isSelected?: boolean;
  onSelectionToggle?: (value?: boolean) => void;
}) {
  const onSelectionToggleMemoized = useRACEffectEvent(onSelectionToggle);

  const tableRowState: TableRowState = useMemo(
    () => ({
      rowKey,
      isSelected,
      onSelectionToggle: onSelectionToggleMemoized,
    }),
    [rowKey, isSelected, onSelectionToggleMemoized],
  );

  return <TableRowStateContext.Provider value={tableRowState}>{children}</TableRowStateContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTableRowState(): TableRowState {
  const tableRowState = useContext(TableRowStateContext);
  if (!tableRowState) {
    throw new Error('Cannot use table row state outside of a table row');
  }

  return tableRowState;
}
